import useAPIClient from '@src/shared/hooks/api/useAPIClient';
import { useQuery } from '@src/shared/query';
import { useInfiniteQuery } from '@tanstack/react-query';
import { usersUrl } from './constants';

export type UserDto = Record<string, unknown>;

export type UsersResponse = {
  results: UserDto[];
  meta: {
    page: number;
    pages: number;
    count: number;
    next: string | null;
    previous: string | null;
  };
};

export type StatusChoicesResponse = Record<string, string>;
export type BranchSearchItem = {
  id: number;
  bran_short: string;
};
export type ManagerSearchItem = {
  id: number;
  name: string;
};
export type RankSearchItem = {
  id: number;
  rank_name: string;
};

type UseUsersParams = {
  pageSize: number;
  ordering?: string;
  filterParams: Record<string, unknown>;
};

export function useUsers({ pageSize, ordering, filterParams }: UseUsersParams) {
  const apiClient = useAPIClient();

  return useInfiniteQuery({
    queryKey: ['root', 'users', 'admin-grid', pageSize, ordering, filterParams],
    initialPageParam: 1,
    queryFn: ({ pageParam, signal }) =>
      apiClient.request({
        url: usersUrl,
        payload: {
          page: String(pageParam),
          page_size: String(pageSize),
          ...filterParams,
          ...(ordering ? { ordering } : {}),
        },
        options: { signal },
      }) as Promise<UsersResponse>,
    getNextPageParam: (lastPage) => {
      if (!lastPage.meta.next) {
        return undefined;
      }
      return lastPage.meta.page + 1;
    },
  });
}

export function useUserStatusChoices() {
  return useQuery<StatusChoicesResponse>('users/choices/user-statuses/', {
    initialData: {},
  });
}

type UseUserBranchChoicesParams = {
  query: string;
  enabled: boolean;
};

export function useUserBranchChoices({
  query,
  enabled,
}: UseUserBranchChoicesParams) {
  return useQuery<BranchSearchItem[]>('users/branch/search/', {
    queryParams: {
      q: query,
    },
    enabled,
  });
}

type UseUserManagerChoicesParams = {
  query: string;
  enabled: boolean;
};

export function useUserManagerChoices({
  query,
  enabled,
}: UseUserManagerChoicesParams) {
  return useQuery<ManagerSearchItem[]>('users/managers/', {
    queryParams: {
      q: query,
    },
    enabled,
  });
}

export function useUserRankChoices() {
  return useQuery<RankSearchItem[]>('users/rank/search/', {
    initialData: [],
  });
}
